import { useState, useEffect } from 'react';
import { fetchHelpCategoryBySlug } from '../api/GlobalApi';

function HelpCategoryPage() {
  const [slug, setSlug] = useState('');
  const [category, setCategory] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // Get slug from URL (you'll need to implement this based on your routing)
    const pathParts = window.location.pathname.split('/');
    const categorySlug = pathParts[pathParts.length - 1];
    setSlug(categorySlug);
    
    if (categorySlug) {
      loadCategory(categorySlug);
    }
  }, []);

  const loadCategory = async (categorySlug) => {
    try {
      setLoading(true);
      const response = await fetchHelpCategoryBySlug(categorySlug);
      setCategory(response.category);
      setArticles(response.articles || []);
    } catch (error) {
      console.error('Error loading category:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      window.location.href = `/help/search?q=${encodeURIComponent(searchQuery)}`;
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const filteredArticles = articles.filter(article =>
    article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.excerpt?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div style={styles.loaderContainer}>
        <div style={styles.spinner}></div>
      </div>
    );
  }

  if (!category) {
    return (
      <div style={styles.notFound}>
        <svg style={styles.notFoundIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h2 style={styles.notFoundTitle}>Category Not Found</h2>
        <p style={styles.notFoundText}>The category you're looking for doesn't exist.</p>
        <button onClick={() => window.location.href = '/help'} style={styles.backBtn}>
          Back to Help Center
        </button>
      </div>
    );
  }

  return (
    <main className="main-content">
      {/* Breadcrumb */}
      <div style={styles.breadcrumb}>
        <a href="/help" style={styles.breadcrumbLink}>Help Center</a>
        <span style={styles.breadcrumbSeparator}>/</span>
        <span style={styles.breadcrumbCurrent}>{category.name}</span>
      </div>

      {/* Category Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.categoryIcon}>
            <svg width="40" height="40" fill="currentColor" viewBox="0 0 16 16">
              <path d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
          </div>
          <div>
            <h1 style={styles.headerTitle}>{category.name}</h1>
            {category.description && (
              <p style={styles.headerDesc}>{category.description}</p>
            )}
            <div style={styles.headerMeta}>
              <span style={styles.articleCount}>
                {articles.length} {articles.length === 1 ? 'article' : 'articles'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Search Box */}
      <div style={styles.container}>
        <div style={styles.searchBox}>
          <svg style={styles.searchIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search articles in this category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            style={styles.searchInput}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} style={styles.clearBtn}>
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
              </svg>
            </button>
          )}
        </div>

        {/* Articles List */}
        {filteredArticles.length > 0 ? (
          <div style={styles.articlesList}>
            {filteredArticles.map((article) => (
              <a
                key={article.id}
                href={`/help/article/${article.slug}`}
                style={styles.articleCard}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                }}
              >
                <div style={styles.articleLeft}>
                  <div style={styles.articleIconWrapper}>
                    <svg style={styles.articleIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div style={styles.articleContent}>
                    <h3 style={styles.articleTitle}>{article.title}</h3>
                    {article.excerpt && (
                      <p style={styles.articleExcerpt}>{article.excerpt}</p>
                    )}
                    <div style={styles.articleFooter}>
                      <span style={styles.viewCount}>
                        <svg width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
                          <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                        </svg>
                        {article.view_count} views
                      </span>
                      {article.is_popular && (
                        <span style={styles.popularBadge}>
                          <svg width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M3.612 15.443c-.386.198-.824-.149-.746-.592l.83-4.73L.173 6.765c-.329-.314-.158-.888.283-.95l4.898-.696L7.538.792c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.282.95l-3.522 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256z"/>
                          </svg>
                          Popular
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <svg style={styles.chevronRight} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>
            ))}
          </div>
        ) : (
          <div style={styles.emptyState}>
            <svg style={styles.emptyIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 style={styles.emptyTitle}>No articles found</h3>
            <p style={styles.emptyText}>
              {searchQuery
                ? `No articles match "${searchQuery}"`
                : 'There are no articles in this category yet.'}
            </p>
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} style={styles.clearSearchBtn}>
                Clear search
              </button>
            )}
          </div>
        )}
      </div>

      {/* Help CTA */}
      <div style={styles.ctaSection}>
        <div style={styles.ctaCard}>
          <svg style={styles.ctaIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 style={styles.ctaTitle}>Can't find what you need?</h3>
          <p style={styles.ctaText}>
            Our support team is ready to help you with any questions
          </p>
          <a href="/contact" style={styles.ctaBtn}>Contact Support</a>
        </div>
      </div>
    </main>
  );
}

const styles = {
  loaderContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '6px solid #ddd',
    borderTop: '6px solid #3498db',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  notFound: {
    textAlign: 'center',
    padding: '80px 20px',
    maxWidth: '600px',
    margin: '0 auto',
  },
  notFoundIcon: {
    width: '80px',
    height: '80px',
    color: '#dadce0',
    margin: '0 auto 24px',
  },
  notFoundTitle: {
    fontSize: '32px',
    fontWeight: '600',
    color: '#202124',
    marginBottom: '12px',
  },
  notFoundText: {
    fontSize: '16px',
    color: '#5f6368',
    marginBottom: '32px',
  },
  backBtn: {
    padding: '12px 32px',
    background: '#1a73e8',
    color: 'white',
    border: 'none',
    borderRadius: '24px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    transition: 'all 0.2s',
  },
  breadcrumb: {
    maxWidth: '1200px',
    margin: '0 auto 32px',
    padding: '0 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
  },
  breadcrumbLink: {
    color: '#1a73e8',
    textDecoration: 'none',
  },
  breadcrumbSeparator: {
    color: '#5f6368',
  },
  breadcrumbCurrent: {
    color: '#5f6368',
  },
  header: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: '60px 20px',
    marginBottom: '40px',
    borderRadius: '20px',
  },
  headerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
    color: 'white',
  },
  categoryIcon: {
    width: '80px',
    height: '80px',
    background: 'rgba(255,255,255,0.2)',
    borderRadius: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: '36px',
    fontWeight: '600',
    marginBottom: '12px',
  },
  headerDesc: {
    fontSize: '18px',
    opacity: 0.95,
    marginBottom: '16px',
    lineHeight: '1.5',
  },
  headerMeta: {
    display: 'flex',
    gap: '16px',
  },
  articleCount: {
    fontSize: '14px',
    opacity: 0.9,
    background: 'rgba(255,255,255,0.2)',
    padding: '6px 16px',
    borderRadius: '12px',
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px 60px',
  },
  searchBox: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '40px',
  },
  searchIcon: {
    position: 'absolute',
    left: '20px',
    width: '20px',
    height: '20px',
    color: '#5f6368',
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    height: '56px',
    padding: '0 56px',
    fontSize: '16px',
    border: '1px solid #dadce0',
    borderRadius: '28px',
    outline: 'none',
    transition: 'all 0.2s',
  },
  clearBtn: {
    position: 'absolute',
    right: '20px',
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    color: '#5f6368',
    borderRadius: '50%',
    transition: 'all 0.2s',
  },
  articlesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  articleCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '24px',
    background: 'white',
    border: '1px solid #e8eaed',
    borderRadius: '12px',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  articleLeft: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '20px',
    flex: 1,
  },
  articleIconWrapper: {
    width: '48px',
    height: '48px',
    background: '#f0f2ff',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  articleIcon: {
    width: '24px',
    height: '24px',
    color: '#667eea',
  },
  articleContent: {
    flex: 1,
  },
  articleTitle: {
    fontSize: '18px',
    fontWeight: '500',
    color: '#202124',
    marginBottom: '8px',
    lineHeight: '1.4',
  },
  articleExcerpt: {
    fontSize: '14px',
    color: '#5f6368',
    lineHeight: '1.5',
    marginBottom: '12px',
  },
  articleFooter: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  viewCount: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#5f6368',
  },
  popularBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    fontWeight: '500',
    color: '#f59e0b',
    background: '#fef3c7',
    padding: '4px 10px',
    borderRadius: '10px',
  },
  chevronRight: {
    width: '24px',
    height: '24px',
    color: '#5f6368',
    flexShrink: 0,
  },
  emptyState: {
    textAlign: 'center',
    padding: '80px 20px',
  },
  emptyIcon: {
    width: '80px',
    height: '80px',
    color: '#dadce0',
    margin: '0 auto 24px',
  },
  emptyTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#202124',
    marginBottom: '12px',
  },
  emptyText: {
    fontSize: '16px',
    color: '#5f6368',
    marginBottom: '24px',
  },
  clearSearchBtn: {
    padding: '10px 24px',
    background: 'white',
    color: '#1a73e8',
    border: '1px solid #1a73e8',
    borderRadius: '20px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s',
  },
  ctaSection: {
    maxWidth: '1200px',
    margin: '0 auto 60px',
    padding: '0 20px',
  },
  ctaCard: {
    textAlign: 'center',
    padding: '60px 32px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '20px',
    color: 'white',
  },
  ctaIcon: {
    width: '56px',
    height: '56px',
    margin: '0 auto 24px',
  },
  ctaTitle: {
    fontSize: '28px',
    fontWeight: '600',
    marginBottom: '16px',
  },
  ctaText: {
    fontSize: '18px',
    opacity: 0.95,
    marginBottom: '32px',
    maxWidth: '600px',
    margin: '0 auto 32px',
  },
  ctaBtn: {
    display: 'inline-block',
    padding: '16px 40px',
    background: 'white',
    color: '#667eea',
    borderRadius: '28px',
    textDecoration: 'none',
    fontWeight: '600',
    fontSize: '16px',
    transition: 'all 0.2s',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
};

const styleSheet = document.createElement('style');
styleSheet.innerHTML = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  @media (hover: hover) {
    input:focus {
      border-color: #1a73e8 !important;
      box-shadow: 0 0 0 3px rgba(26,115,232,0.1) !important;
    }

    button:hover {
      background: #f1f3f4 !important;
    }

    .backBtn:hover, .clearSearchBtn:hover {
      background: #1557b0 !important;
      transform: translateY(-2px);
    }

    .ctaBtn:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(0,0,0,0.2) !important;
    }
  }

  @media (max-width: 768px) {
    .headerContent {
      flex-direction: column !important;
      text-align: center;
    }

    h1 {
      font-size: 28px !important;
    }
  }
`;
document.head.appendChild(styleSheet);

export default HelpCategoryPage;